const ANCHOR_START = "__start__"

function defaultNormaliseText(value) {
    return String(value ?? "").trim()
}

function defaultBuildTaskChangeSummary() {
    return []
}

export function buildSideBySideTaskRows({
    originalTasks = [],
    draftTasks = [],
    originalTaskLookup,
    draftTaskLookup,
    changedTaskIds = new Set(),
    amendsByKey = {},
    normaliseText = defaultNormaliseText,
    buildTaskChangeSummary = defaultBuildTaskChangeSummary,
}) {
    const headerPairings = pairHeaders(originalTasks, draftTasks, { normaliseText })
    const removedByAnchor = new Map()

    function enqueueRemoved(anchor, row) {
        if (!removedByAnchor.has(anchor)) removedByAnchor.set(anchor, [])
        removedByAnchor.get(anchor).push(row)
    }

    let lastPairedKey = ANCHOR_START
    for (const [index, task] of originalTasks.entries()) {
        if (task.isHeader) {
            const pairing = headerPairings.byOriginalIndex.get(index)
            if (pairing) {
                lastPairedKey = pairing.headerKey
                continue
            }
            enqueueRemoved(lastPairedKey, {
                key: buildHeaderKey(task, index, { normaliseText }),
                label: normaliseText(task.title) || `Header ${index + 1}`,
                status: "removed",
                isHeader: true,
                originalTask: task,
                draftTask: null,
                note: getHeaderNote(task, amendsByKey),
            })
            continue
        }

        const taskKey = task.taskId ?? task.id
        if (!taskKey) continue
        const draftTask = draftTaskLookup.get(taskKey)
        if (draftTask) {
            lastPairedKey = taskKey
            continue
        }

        enqueueRemoved(lastPairedKey, {
            key: taskKey,
            label: normaliseText(task.title) || taskKey,
            status: "removed",
            originalTask: task,
            draftTask: null,
            note: amendsByKey[`tasks.${taskKey}`],
            displayIndex: task.displayIndex ?? null,
            changeSummary: buildTaskChangeSummary(task, null),
        })
    }

    const rows = []

    function flushRemoved(anchor) {
        const queued = removedByAnchor.get(anchor)
        if (!queued) return
        rows.push(...queued)
        removedByAnchor.delete(anchor)
    }

    flushRemoved(ANCHOR_START)

    for (const [index, task] of draftTasks.entries()) {
        if (task.isHeader) {
            const pairing = headerPairings.byDraftIndex.get(index)
            const originalHeader = pairing?.originalTask ?? null
            const headerKey = pairing?.headerKey ?? buildHeaderKey(task, index, { normaliseText })
            rows.push({
                key: headerKey,
                label: normaliseText(task.title) || `Header ${index + 1}`,
                status: getHeaderStatus(originalHeader, task, changedTaskIds),
                isHeader: true,
                originalTask: originalHeader,
                draftTask: task,
                note: getHeaderNote(task, amendsByKey),
            })
            flushRemoved(headerKey)
            continue
        }

        const taskKey = task.taskId ?? task.id
        if (!taskKey) continue

        const originalTask = originalTaskLookup.get(taskKey)
        const hasTaskChanges = changedTaskIds.has(taskKey)
        const status = !originalTask ? "new" : hasTaskChanges ? "modified" : "unchanged"

        rows.push({
            key: taskKey,
            label: normaliseText(task.title) || taskKey,
            status,
            originalTask: originalTask ?? null,
            draftTask: task,
            note: amendsByKey[`tasks.${taskKey}`],
            displayIndex: task.displayIndex ?? originalTask?.displayIndex ?? null,
            changeSummary: buildTaskChangeSummary(originalTask ?? null, task),
        })

        flushRemoved(taskKey)
    }

    for (const queued of removedByAnchor.values()) rows.push(...queued)

    return rows
}

export function detectTaskOrderEntries(
    originalTasks = [],
    draftTasks = [],
    { normaliseText = defaultNormaliseText } = {}
) {
    const originalPositions = {}
    const entries = []
    let originalIndex = 0

    for (const task of originalTasks) {
        if (task.isHeader) continue
        originalIndex += 1
        originalPositions[task.taskId ?? task.id] = originalIndex
    }

    let draftIndex = 0
    for (const task of draftTasks) {
        if (task.isHeader) continue
        draftIndex += 1
        const taskKey = task.taskId ?? task.id
        const originalPosition = originalPositions[taskKey]
        if (originalPosition == null) {
            entries.push({
                key: taskKey,
                title: buildMovementTitle(task, { normaliseText }),
                from: null,
                to: draftIndex,
            })
            continue
        }
        if (originalPosition !== draftIndex) {
            entries.push({
                key: taskKey,
                title: buildMovementTitle(task, { normaliseText }),
                from: originalPosition,
                to: draftIndex,
            })
        }
    }

    return entries
}

export function buildTaskOrderChangesSection({
    orderEntries = [],
    note = null,
    normaliseText = defaultNormaliseText,
} = {}) {
    return {
        title: "Task Order Changes",
        items: orderEntries
            .slice()
            .sort((a, b) => a.to - b.to)
            .map((entry) => ({
                key: entry.key,
                title: normaliseText(entry.title),
                previousOrder: entry.from ?? "★",
            })),
        note,
    }
}

export function buildHeaderQueues(tasks = [], { normaliseText = defaultNormaliseText } = {}) {
    return tasks.reduce((queues, task) => {
        if (!task?.isHeader) return queues
        const title = normaliseText(task?.title)
        if (!queues[title]) queues[title] = []
        queues[title].push(task)
        return queues
    }, {})
}

export function shiftHeader(queues, title, { normaliseText = defaultNormaliseText } = {}) {
    const queue = queues?.[normaliseText(title) || ""]
    if (!queue?.length) return null
    return queue.shift()
}

export function buildHeaderKey(task, index, { normaliseText = defaultNormaliseText } = {}) {
    return `header:${normaliseText(task?.title) || "untitled"}:${index}`
}

function getHeaderId(task) {
    return task?.isHeader ? task.headerTaskId : null
}

// Pairs headers by id, as the review page does, so a renamed header is one modified row rather
// than a removed and a new one. A published header with no id (an older one) pairs by title with
// any draft header still unpaired, as the draft gives it an id the first time it is saved.
export function pairHeaders(originalTasks, draftTasks, { normaliseText = defaultNormaliseText } = {}) {
    const byOriginalIndex = new Map()
    const byDraftIndex = new Map()
    const draftIndexById = new Map()

    for (const [index, task] of draftTasks.entries()) {
        const headerId = getHeaderId(task)
        if (headerId != null) draftIndexById.set(headerId, index)
    }

    for (const [index, task] of originalTasks.entries()) {
        const headerId = getHeaderId(task)
        if (headerId != null) pair(index, draftIndexById.get(headerId))
    }

    const unpairedDraftHeaders = draftTasks.filter((task, index) => task?.isHeader && !byDraftIndex.has(index))
    const draftHeaderQueues = buildHeaderQueues(unpairedDraftHeaders, { normaliseText })
    for (const [index, task] of originalTasks.entries()) {
        if (!hasNoHeaderId(task)) continue
        pair(index, draftTasks.indexOf(shiftHeader(draftHeaderQueues, task.title, { normaliseText })))
    }

    return { byOriginalIndex, byDraftIndex }

    function pair(originalIndex, draftIndex) {
        if (draftIndex == null || draftIndex < 0) return
        const draftHeader = draftTasks[draftIndex]
        const headerKey = buildHeaderKey(draftHeader, draftIndex, { normaliseText })
        const pairing = {
            headerKey,
            originalTask: originalTasks[originalIndex],
            draftTask: draftHeader,
            originalIndex,
            draftIndex,
        }
        byOriginalIndex.set(originalIndex, pairing)
        byDraftIndex.set(draftIndex, pairing)
    }
}

function hasNoHeaderId(task) {
    return task?.isHeader && getHeaderId(task) == null
}

// A published header with no id was paired by its unchanged title, so it is unchanged even though
// the draft's newly saved id makes it look added in changedTaskIds
function getHeaderStatus(originalHeader, draftHeader, changedTaskIds) {
    if (!originalHeader) return "new"
    if (getHeaderId(originalHeader) == null) return "unchanged"
    const headerId = getHeaderId(draftHeader)
    return headerId != null && changedTaskIds.has(headerId) ? "modified" : "unchanged"
}

// Header author notes are stored against the same tasks.<id> key as task notes
function getHeaderNote(header, amendsByKey) {
    const headerId = getHeaderId(header)
    return headerId ? amendsByKey[`tasks.${headerId}`] ?? null : null
}

export function buildMovementTitle(task, { normaliseText = defaultNormaliseText } = {}) {
    const prefix = task?.displayIndex ? `${task.displayIndex}. ` : ""
    return `${prefix}${normaliseText(task?.title) || task?.taskId || task?.id}`
}
